import * as React from "react";
import { Printer } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";
import QRCode from "qrcode";

import anthemLogo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { api } from "$convex/convexApi";
import "./page.css";


function splitTextByWords(value: string | null | undefined, maxLineLength = 24): string[] {
  if (!value) return [];
  if (value.length <= maxLineLength) return [value];

  const words = value.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    if ((currentLine + word).length > maxLineLength) {
      if (currentLine.trim()) lines.push(currentLine.trim());
      currentLine = `${word} `;
    } else {
      currentLine += `${word} `;
    }
  }

  if (currentLine.trim()) lines.push(currentLine.trim());
  return lines;
}

function formatWarehouseLocation(value: unknown): string {
  if (!value) return "Kansas City";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const location = value as { name?: unknown; companyName?: unknown; state?: unknown };
    if (typeof location.name === "string" && location.name.trim()) return location.name;
    if (typeof location.companyName === "string" && location.companyName.trim()) return location.companyName;
    if (typeof location.state === "string" && location.state.trim()) return location.state;
  }
  return "Kansas City";
}

export default function CargoLabelPage() {
  const [searchParams] = useSearchParams();
  const cargoId = searchParams.get("id");
  const qrCanvasRef = React.useRef<HTMLCanvasElement | null>(null);

  const cargoData = useQuery(
    api.cargos.getCargoLabelData,
    cargoId
      ? {
          cargoId: cargoId as any,
        }
      : "skip",
  ) as any;

  const isLoading = cargoId ? cargoData === undefined : false;

  const productLines = React.useMemo(
    () => splitTextByWords(cargoData?.productName),
    [cargoData?.productName],
  );

  const varietalLines = React.useMemo(
    () => splitTextByWords(cargoData?.varietal),
    [cargoData?.varietal],
  );

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = `Cargo Label${cargoData?.ociNumber ? ` - ${cargoData.ociNumber}` : ""}`;

    let robotsMeta = document.querySelector("meta[name='robots']") as HTMLMetaElement | null;
    const hadRobotsMeta = Boolean(robotsMeta);
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      document.title = previousTitle;
      if (robotsMeta && !hadRobotsMeta) {
        robotsMeta.remove();
      }
    };
  }, [cargoData?.ociNumber]);

  React.useEffect(() => {
    if (!cargoData?.url || !qrCanvasRef.current) return;

    void QRCode.toCanvas(qrCanvasRef.current, cargoData.url, {
      errorCorrectionLevel: "H",
      width: 100,
      margin: 1,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    });
  }, [cargoData?.url]);

  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  if (!cargoId) {
    return (
      <main className="cargo-label-page">
        <div className="error-container">
          <h2>Missing Cargo ID</h2>
          <p>
            Please provide a cargo ID in the URL: <code>?id=...</code>
          </p>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="cargo-label-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading cargo data...</p>
        </div>
      </main>
    );
  }

  if (!cargoData) {
    return (
      <main className="cargo-label-page">
        <div className="error-container">
          <h2>Cargo Not Found</h2>
          <p>Could not find cargo with ID: {cargoId}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="cargo-label-page">
      <div className="page-container">
        <div className="print-controls no-print">
          <Button type="button" onClick={handlePrint} className="print-button">
            <Printer className="mr-2 h-4 w-4" />
            Print Label
          </Button>
          <div className="info-text">
            {cargoData.ociNumber || cargoData.contractId || "No OCI"} - {cargoData.productName}
          </div>
        </div>

        <div className="label">
          <div className="logo-container">
            <img src={anthemLogo} alt="Anthem Coffee Imports" className="anthem-logo" />
          </div>

          <div className="aci-number">ACI # {cargoData.contractId || cargoData.ociNumber || "N/A"}</div>

          {cargoData.countryOfOrigin ? <div className="country">{cargoData.countryOfOrigin}</div> : null}

          <div className="product-name">
            {productLines.map((line, index) => (
              <div key={`${line}-${index}`}>{line}</div>
            ))}
          </div>

          {cargoData.millingProcess ? <div className="detail-text">{cargoData.millingProcess}</div> : null}

          {cargoData.varietal ? (
            <div className="detail-text">
              {varietalLines.map((line, index) => (
                <div key={`${line}-${index}`}>{line}</div>
              ))}
            </div>
          ) : null}

          {cargoData.elevationMasl ? <div className="detail-text">{cargoData.elevationMasl} MASL</div> : null}

          {cargoData.weight ? <div className="detail-text">{cargoData.weight} lb Bag</div> : null}

          <div className="detail-text">{formatWarehouseLocation(cargoData.warehouseLocation)}</div>

          <div className="bottom-section">
            {cargoData.url ? (
              <div className="qr-container">
                <canvas ref={qrCanvasRef} className="qr-code" />
                <div className="qr-logo-overlay">
                  <img src={anthemLogo} alt="Anthem" className="qr-logo" />
                </div>
              </div>
            ) : null}

            <div className="footer">
              <div className="footer-company">Anthem Coffee Imports</div>
              <div className="footer-phone">913.856.3480</div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
